import { useContext, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { FiMap } from "react-icons/fi";
import { LuSatellite } from "react-icons/lu";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { mountImperativeTooltip } from "../common/Tooltip";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";

const MapTypeControl = ({ mapType, setMapType, isRtl = false, isMobileViewport = false, showDatePanel = false }) => {
  const map = useMap();
  const { langMode } = useContext(AppContext);
  const [zoom, setZoom] = useState(() => (map ? map.getZoom() : 0));
  const controlRef = useRef(null);
  const containerElRef = useRef(null);
  const mapBtnRef = useRef(null);
  const satBtnRef = useRef(null);
  const zoomInBtnRef = useRef(null);
  const zoomOutBtnRef = useRef(null);
  const zoomLevelRef = useRef(null);
  const mapBtnRootRef = useRef(null);
  const satBtnRootRef = useRef(null);
  const unmountTimerIdsRef = useRef([]);
  const tooltipCleanupsRef = useRef([]);

  const scheduleSafeUnmount = (rootRef) => {
    const root = rootRef.current;
    rootRef.current = null;
    if (!root) return;

    const timerId = window.setTimeout(() => {
      try {
        root.unmount();
      } catch (error) {
        // Swallow cleanup races in dev strict-mode render cycles.
      }
    }, 0);
    unmountTimerIdsRef.current.push(timerId);
  };

  useEffect(() => {
    if (!map) return;

    const onZoomEnd = () => {
      setZoom(map.getZoom());
    };

    map.on("zoomend", onZoomEnd);
    return () => map.off("zoomend", onZoomEnd);
  }, [map]);

  useEffect(() => {
    if (!map) return;
    // Lives on the content side, same as the icon rail (MapViewToolbar) it
    // stacks below: right in LTR, left in RTL. Mobile keeps the original
    // top-left anchor, since the icon rail moves to a bottom row there
    // instead of staying stacked with this control.
    const control = L.control({
      position: isMobileViewport ? "topleft" : (isRtl ? "topleft" : "topright"),
    });

    control.onAdd = () => {
      const container = L.DomUtil.create("div", "custom-zoom map-type-zoom-control");
      containerElRef.current = container;

      const mapTypeGroup = L.DomUtil.create("div", "map-control-group", container);
      const zoomDivider = L.DomUtil.create("div", "map-control-divider", container);
      const zoomGroup = L.DomUtil.create("div", "map-control-group", container);

      const mapBtn = L.DomUtil.create("button", "custom-btn", mapTypeGroup);
      const satBtn = L.DomUtil.create("button", "custom-btn", mapTypeGroup);
      const zoomInBtn = L.DomUtil.create("button", "custom-btn", zoomGroup);
      const zoomLevel = L.DomUtil.create("div", "custom-zoom-level", zoomGroup);
      const zoomOutBtn = L.DomUtil.create("button", "custom-btn", zoomGroup);
      mapBtnRef.current = mapBtn;
      satBtnRef.current = satBtn;
      zoomInBtnRef.current = zoomInBtn;
      zoomOutBtnRef.current = zoomOutBtn;
      zoomLevelRef.current = zoomLevel;

      mapBtn.onclick = () => setMapType("map");
      satBtn.onclick = () => setMapType("satellite");
      zoomInBtn.onclick = () => map.zoomIn();
      zoomOutBtn.onclick = () => map.zoomOut();
      zoomInBtn.textContent = "+";
      zoomOutBtn.textContent = "-";
      zoomLevel.textContent = `${map.getZoom()}x`;
      zoomDivider.setAttribute("aria-hidden", "true");

      // Render React Icons
      mapBtnRootRef.current = createRoot(mapBtn);
      satBtnRootRef.current = createRoot(satBtn);
      mapBtnRootRef.current.render(<FiMap size={15} />);
      satBtnRootRef.current.render(<LuSatellite size={18} />);

      // Tooltips should always open into the map, away from whichever edge
      // this control is docked to.
      const tooltipPosition = isMobileViewport || isRtl ? "right" : "left";
      tooltipCleanupsRef.current = [
        mountImperativeTooltip(mapBtn, translations[langMode]?.mapTipMapView ?? "Map view", { position: tooltipPosition, rtl: isRtl }),
        mountImperativeTooltip(satBtn, translations[langMode]?.mapTipSatellite ?? "Satellite view", { position: tooltipPosition, rtl: isRtl }),
        mountImperativeTooltip(zoomInBtn, translations[langMode]?.mapTipZoomIn ?? "Zoom in", { position: tooltipPosition, rtl: isRtl }),
        mountImperativeTooltip(zoomOutBtn, translations[langMode]?.mapTipZoomOut ?? "Zoom out", { position: tooltipPosition, rtl: isRtl }),
      ];

      L.DomEvent.disableClickPropagation(container);

      return container;
    };

    control.addTo(map);
    controlRef.current = control;

    return () => {
      if (controlRef.current) {
        controlRef.current.remove();
        controlRef.current = null;
      }
      tooltipCleanupsRef.current.forEach((cleanup) => cleanup());
      tooltipCleanupsRef.current = [];
      scheduleSafeUnmount(mapBtnRootRef);
      scheduleSafeUnmount(satBtnRootRef);
      mapBtnRef.current = null;
      satBtnRef.current = null;
      zoomInBtnRef.current = null;
      zoomOutBtnRef.current = null;
      zoomLevelRef.current = null;
      containerElRef.current = null;
    };
  }, [map, setMapType, isRtl, isMobileViewport, langMode]);

  // Keep the gap below the Tracking button responsive: measure its real
  // rendered position instead of relying on hardcoded CSS margins, so the
  // control always sits just under it regardless of viewport size, date
  // panel state, or label length.
  useEffect(() => {
    if (!map) return undefined;

    const updateSpacing = () => {
      const container = containerElRef.current;
      const mapEl = map.getContainer?.();
      if (!container || !mapEl) return;

      const mapRect = mapEl.getBoundingClientRect();
      const corner = container.parentElement; // .leaflet-top.leaflet-right/-left
      const cornerRect = corner ? corner.getBoundingClientRect() : mapRect;

      if (!isMobileViewport) {
        // Desktop: this control now lives on the content side (right in LTR,
        // left in RTL), same as the icon rail (MapViewToolbar) - stack
        // directly below it. No horizontal margin needed: CSS zeroes
        // margin-left/-right for .leaflet-top.leaflet-right/-left .custom-zoom,
        // and the shared pane offset (applied elsewhere for showDetailPanel)
        // already lines this control's edge up with the icon rail's.
        //
        // Deliberately NOT measured from the rail's own live top: that value
        // is itself set asynchronously by MapViewToolbar's own effect, and
        // reading it mid-flight (e.g. right as the bottom Analytics panel
        // opens/closes) can capture a stale in-between position and inherit
        // its error, ballooning this gap. The rail's top is really just
        // `trackingBottom + gap`, and its height is intrinsic/stable, so
        // compute the same way the rail itself does instead of reading its
        // rendered position back.
        const trackingEl = mapEl.querySelector(".map-tracking-anchor");
        const toolbarRailEl = mapEl.querySelector(".map-view-toolbar");
        if (!trackingEl || !toolbarRailEl) return;

        const gap = 12;
        const trackingBottom = trackingEl.getBoundingClientRect().bottom - mapRect.top;
        const railHeight = toolbarRailEl.getBoundingClientRect().height;
        const desiredTop = trackingBottom + gap + railHeight + gap;
        // Use the pane's target top (set inline by AzureZoomControl) rather than its live, mid-slide position.
        const paneTop = corner && corner.style.top ? parseFloat(corner.style.top) : cornerRect.top - mapRect.top;
        const neededMargin = desiredTop - paneTop;
        container.style.marginTop = `${Math.max(0, Math.round(neededMargin))}px`;
        return;
      }

      // Mobile: unchanged - Tracking lives inline inside the same top-left
      // toolbar as the map/list toggle and status pills (there's no separate
      // .map-tracking-anchor there), so anchor below that whole toolbar's
      // real bottom edge instead - it can wrap onto several rows on narrow
      // screens, which is exactly why this needs to be measured live rather
      // than a fixed offset.
      const topLeftRowEl = mapEl.querySelector(".map-top-left-toolbar");
      if (!topLeftRowEl) return;

      const anchorBottom = topLeftRowEl.getBoundingClientRect().bottom;
      const gapBelowTracking = 12;

      const desiredTop = anchorBottom - mapRect.top + gapBelowTracking;
      const neededMargin = desiredTop - (cornerRect.top - mapRect.top);
      container.style.marginTop = `${Math.max(0, Math.round(neededMargin))}px`;

      // Line this control's left edge up with the Map/List/status-pill row's
      // left edge (mobile always docks this control top-left).
      // NOTE: index.css has a global `.leaflet-left .leaflet-control { margin-left:
      // 16px !important }` rule (Leaflet auto-adds the leaflet-control class to
      // every control, so this matches ours too) - a plain inline style can't
      // beat that !important, so this must also be set with "important" priority.
      const rowRect = topLeftRowEl.getBoundingClientRect();
      const desiredLeft = rowRect.left - mapRect.left;
      const neededMarginLeft = desiredLeft - (cornerRect.left - mapRect.left);
      container.style.setProperty(
        "margin-left",
        `${Math.max(0, Math.round(neededMarginLeft))}px`,
        "important"
      );
    };

    updateSpacing();
    const timeoutIds = [
      window.setTimeout(updateSpacing, 0),
      window.setTimeout(updateSpacing, 180),
      window.setTimeout(updateSpacing, 360),
    ];

    window.addEventListener("resize", updateSpacing);

    const mapEl = map.getContainer?.();
    let resizeObserver = null;
    if (mapEl && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateSpacing);
      resizeObserver.observe(mapEl);
      if (isMobileViewport) {
        const topLeftRowEl = mapEl.querySelector(".map-top-left-toolbar");
        if (topLeftRowEl) resizeObserver.observe(topLeftRowEl);
      } else {
        const trackingEl = mapEl.querySelector(".map-tracking-anchor");
        if (trackingEl) resizeObserver.observe(trackingEl);
        const toolbarRailEl = mapEl.querySelector(".map-view-toolbar");
        if (toolbarRailEl) resizeObserver.observe(toolbarRailEl);
      }
    }

    // The corner pane, Tracking anchor and icon rail slide (CSS transitions) when the date bar
    // opens/closes; re-measure once they settle so this control isn't left at a mid-slide position.
    const transitionTargets = [
      containerElRef.current?.parentElement,
      mapEl?.querySelector(".map-tracking-anchor"),
      mapEl?.querySelector(".map-view-toolbar"),
    ].filter(Boolean);
    transitionTargets.forEach((el) => el.addEventListener("transitionend", updateSpacing));

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      window.removeEventListener("resize", updateSpacing);
      if (resizeObserver) resizeObserver.disconnect();
      transitionTargets.forEach((el) => el.removeEventListener("transitionend", updateSpacing));
    };
  }, [map, isRtl, isMobileViewport, showDatePanel]);

  useEffect(() => {
    return () => {
      unmountTimerIdsRef.current.forEach((id) => window.clearTimeout(id));
      unmountTimerIdsRef.current = [];
    };
  }, []);

  useEffect(() => {
    const mapBtn = mapBtnRef.current;
    const satBtn = satBtnRef.current;
    if (!mapBtn || !satBtn) return;

    mapBtn.classList.toggle("active", mapType === "map");
    satBtn.classList.toggle("active", mapType === "satellite");
  }, [mapType]);

  useEffect(() => {
    if (!zoomLevelRef.current) return;
    zoomLevelRef.current.textContent = `${zoom}x`;
  }, [zoom]);

  return null;
};

export default MapTypeControl;
